import { useCallback } from 'react';
import { AuiIf, ComposerPrimitive } from '@assistant-ui/react';
import CloseIcon from '@mui/icons-material/Close';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import { ButtonBase } from '@mui/material';

import { Toast } from '#components/toast/toast.js';
import { useAddToast } from '#state/stores/toast-store.js';
import { useAttachmentStore } from './attachment-store-context.js';
import { StopRunButton } from './stop-run-button.js';
import {
	ACCEPTED_IMAGE_TYPES,
	ACCEPTED_DOCUMENT_TYPES,
	MAX_ATTACHMENT_SIZE_BYTES,
	type Attachment,
} from './attachment-store.js';

interface WorkbenchComposerProps {
	placeholder: string;
	/** Optional element rendered in the composer row before the send/stop button (e.g. a usage indicator). */
}

/** Result of attempting to read a dropped/pasted file as an attachment. */
type ReadAttachmentResult = { attachment: Attachment } | { error: string };

const MAX_ATTACHMENT_SIZE_MB = Math.round(MAX_ATTACHMENT_SIZE_BYTES / (1024 * 1024));

/**
 * Reads a `File` as a base64-encoded `Attachment`. Returns an `error` reason
 * (suitable for surfacing to the user) when the file is an unsupported type,
 * is empty, exceeds `MAX_ATTACHMENT_SIZE_BYTES`, or cannot be read.
 *
 * @param file - The file to read.
 * @returns The resolved `Attachment`, or an `error` describing why it was rejected.
 */
async function readFileAsAttachment(file: File): Promise<ReadAttachmentResult> {
	if (!ACCEPTED_IMAGE_TYPES.has(file.type) && !ACCEPTED_DOCUMENT_TYPES.has(file.type)) {
		return { error: `"${file.name}" is not a supported file type.` };
	}
	// A zero-byte file encodes to an empty base64 string, which the backend
	// attachment schema rejects. Reject it here so both sides agree.
	if (file.size === 0) {
		return { error: `"${file.name}" is empty.` };
	}
	if (file.size > MAX_ATTACHMENT_SIZE_BYTES) {
		return { error: `"${file.name}" exceeds the ${MAX_ATTACHMENT_SIZE_MB} MB attachment limit.` };
	}
	return new Promise((resolve) => {
		const reader = new FileReader();
		reader.onload = () => {
			if (typeof reader.result !== 'string') {
				resolve({ error: `"${file.name}" could not be read.` });
				return;
			}
			const dataUri = reader.result;
			const commaIndex = dataUri.indexOf(',');
			resolve({
				attachment: {
					id: Math.random().toString(36).slice(2),
					name: file.name,
					mediaType: file.type,
					data: dataUri.slice(commaIndex + 1),
					previewUrl: dataUri,
				},
			});
		};
		reader.onerror = () => resolve({ error: `"${file.name}" could not be read.` });
		reader.readAsDataURL(file);
	});
}

/**
 * Workbench input bar styled as a compact prompt row. Supports pasting or dragging
 * images and PDF files as attachments.
 *
 * @param props.placeholder - Placeholder text for the textarea.
 */
export function WorkbenchComposer({ placeholder }: WorkbenchComposerProps) {
	const attachments = useAttachmentStore((s) => s.pending);
	const addAttachment = useAttachmentStore((s) => s.add);
	const addToast = useAddToast();

	const addFileAttachment = useCallback(
		async (file: File): Promise<void> => {
			const result = await readFileAsAttachment(file);
			if ('attachment' in result) {
				addAttachment(result.attachment);
			} else {
				addToast(
					<Toast severity='error' title='Attachment skipped'>
						{result.error}
					</Toast>,
				);
			}
		},
		[addAttachment, addToast],
	);

	const handlePaste = useCallback(
		async (e: React.ClipboardEvent) => {
			const items = Array.from(e.clipboardData.items);
			const fileItems = items.filter(
				(item) => ACCEPTED_IMAGE_TYPES.has(item.type) || ACCEPTED_DOCUMENT_TYPES.has(item.type),
			);
			if (fileItems.length === 0) return;

			for (const item of fileItems) {
				const file = item.getAsFile();
				if (!file) continue;
				await addFileAttachment(file);
			}
		},
		[addFileAttachment],
	);

	const handleDrop = useCallback(
		async (e: React.DragEvent) => {
			e.preventDefault();
			const files = Array.from(e.dataTransfer.files);
			for (const file of files) {
				await addFileAttachment(file);
			}
		},
		[addFileAttachment],
	);

	const handleDragOver = useCallback((e: React.DragEvent) => {
		e.preventDefault();
	}, []);

	return (
		<div className='shrink-0 border-t border-gray-a4 bg-gray-2' onDrop={handleDrop} onDragOver={handleDragOver}>
			{attachments.length > 0 && (
				<div className='flex flex-wrap gap-2 px-3 pt-3 pl-[68px]'>
					{attachments.map((att) => (
						<AttachmentPreview key={att.id} attachment={att} />
					))}
				</div>
			)}
			<ComposerPrimitive.Root
				className='grid grid-cols-[46px_minmax(0,1fr)_auto] items-center gap-x-2.5 px-3 py-2.5'
				onPaste={handlePaste}
			>
				<span className='flex h-8 items-center justify-end text-blue-11 select-none'>
					<ChevronRightRoundedIcon fontSize='small' />
				</span>
				<ComposerPrimitive.Input
					className='block min-h-8 min-w-0 resize-none overflow-y-auto bg-transparent py-1.5 text-sm leading-5 text-gray-12 outline-none placeholder:text-gray-a9 disabled:cursor-not-allowed disabled:opacity-50'
					placeholder={placeholder}
					minRows={1}
					maxRows={6}
				/>
				<div className='flex h-8 items-center justify-end gap-1.5'>
					<AuiIf condition={(s) => s.thread.isRunning}>
						<StopRunButton />
					</AuiIf>
					<AuiIf condition={(s) => !s.thread.isRunning}>
						<ComposerPrimitive.Send asChild>
							<ButtonBase
								aria-label='Send message'
								className='inline-flex h-8 w-8 items-center justify-center text-blue-a9 select-none hover:text-blue-11 disabled:opacity-30'
							>
								<SendRoundedIcon fontSize='small' />
							</ButtonBase>
						</ComposerPrimitive.Send>
					</AuiIf>
				</div>
			</ComposerPrimitive.Root>
		</div>
	);
}

interface AttachmentPreviewProps {
	attachment: Attachment;
}

/**
 * Thumbnail or label chip for a pending attachment with a remove button.
 *
 * @param attachment - The attachment to preview.
 */
function AttachmentPreview({ attachment }: AttachmentPreviewProps) {
	const removeAttachment = useAttachmentStore((s) => s.remove);
	const isImage = ACCEPTED_IMAGE_TYPES.has(attachment.mediaType);

	return (
		<div className='flex items-center gap-1.5 rounded border border-gray-a4 bg-gray-a2 p-1 pr-1.5 text-xs'>
			{isImage && <img src={attachment.previewUrl} alt={attachment.name} className='h-8 w-8 rounded object-cover' />}
			<span className='truncate max-w-32 text-gray-a11'>{attachment.name}</span>
			<ButtonBase
				onClick={() => removeAttachment(attachment.id)}
				className='ml-0.5 shrink-0 text-gray-a7 hover:text-gray-a11'
				aria-label={`Remove ${attachment.name}`}
			>
				<CloseIcon style={{ fontSize: 14 }} />
			</ButtonBase>
		</div>
	);
}
